import React, { useEffect, useMemo, useRef, useState } from "react";
import { Music, MoreVertical, Download, Copy, Check, RefreshCw, Trash2, Loader2, Lock } from "lucide-react";


export default function SongDetailView({ song, onClose, api, onDelete, onRecreate, onGenerateVideo, onGenerateClip }) {
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [lyricsTimeline, setLyricsTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [stylesExpanded, setStylesExpanded] = useState(false);
  const [timelineError, setTimelineError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  // One-shot inline notice ("Song is still streaming…") for locked actions.
  // Auto-clears after 3.5s so it never sticks around.
  const [notice, setNotice] = useState(null);
  const noticeTimerRef = useRef(null);
  const menuRef = useRef(null);
  const karaokeRef = useRef(null);
  const songIdRef = useRef(song.id);

  // A song is still being generated and only playable via the streaming URL.
  // In this state we block downloads AND block video/clip generation because
  // the underlying audio file isn't finalized yet.
  const isStreaming = song.status === 'streaming';

  const showStreamingNotice = (action) => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    setNotice(
      action === 'download'
        ? "Hang tight — this song is still streaming. You'll be able to download it once generation finishes."
        : "This song is still streaming. Wait until generation finishes to use it for video or clip creation."
    );
    noticeTimerRef.current = setTimeout(() => setNotice(null), 3500);
  };

  useEffect(() => () => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
  }, []);

  const displayTitle = song.title || 'Untitled';

  useEffect(() => {
    if (songIdRef.current !== song.id) {
      songIdRef.current = song.id;
      setLyricsTimeline(null);
      setTimelineLoading(false);
      setTimelineError(null);
      setCurrentTime(0);
      setIsPlaying(false);
    }
  }, [song.id]);

  useEffect(() => {
    if (song.lyrics_timeline && song.lyrics_timeline.length > 0) {
      setLyricsTimeline(song.lyrics_timeline);
      return;
    }

    if (song.provider === 'sonic' && song.clip_id && !lyricsTimeline && !timelineLoading) {
      setTimelineLoading(true);
      api.post('/music/lyrics-timeline', { clip_id: song.clip_id, task_id: song.id })
        .then(r => {
          if (r.data?.alignment && r.data.alignment.length > 0) {
            setLyricsTimeline(r.data.alignment);
          } else {
            setTimelineError('No karaoke available');
          }
          setTimelineLoading(false);
        })
        .catch(e => {
          setTimelineError('Could not load karaoke');
          setTimelineLoading(false);
        });
    }
  }, [song, api, lyricsTimeline, timelineLoading]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  const lyricsLines = useMemo(() => {
    if (!lyricsTimeline || lyricsTimeline.length === 0) return [];

    const lines = [];
    let currentLine = { words: [], start_s: null, end_s: null };

    for (const word of lyricsTimeline) {
      if (word.word.includes('\n')) {
        const parts = word.word.split('\n');
        parts.forEach((part, idx) => {
          const isLastPart = idx === parts.length - 1;

          if (part.trim() || idx === 0) {
            const partWord = {
              ...word,
              word: part,
              partial: true,
              start_s: isLastPart ? word.start_s : null,
              end_s: isLastPart ? word.end_s : null
            };
            currentLine.words.push(partWord);

            if (isLastPart) {
              if (currentLine.start_s === null) currentLine.start_s = word.start_s;
              currentLine.end_s = word.end_s;
            }
          }
          if (idx < parts.length - 1 && currentLine.words.length > 0) {
            lines.push(currentLine);
            currentLine = { words: [], start_s: null, end_s: null };
          }
        });
      } else {
        currentLine.words.push(word);
        if (currentLine.start_s === null) currentLine.start_s = word.start_s;
        currentLine.end_s = word.end_s;
      }
    }

    if (currentLine.words.length > 0) {
      lines.push(currentLine);
    }

    const filteredLines = lines.filter(line => {
      const lineText = line.words.map(w => w.word).join('').trim();
      if (!lineText) return false;
      if (/^\[.*\]$/.test(lineText)) return false;
      return true;
    });

    return filteredLines.map(line => {
      const wordsWithTiming = line.words.filter(w => w.start_s !== undefined && w.end_s !== undefined);
      if (wordsWithTiming.length === 0) return line;

      const lastWord = wordsWithTiming[wordsWithTiming.length - 1];

      return {
        ...line,
        start_s: line.start_s,
        end_s: line.end_s,
        singing_end_s: lastWord.end_s,
      };
    });
  }, [lyricsTimeline]);

  const { activeLineIndex, focusedLineIndex, countdown, lineProgress, sungCharsInLine } = useMemo(() => {
    if (!lyricsLines.length) return { activeLineIndex: -1, focusedLineIndex: -1, countdown: null, lineProgress: 0, sungCharsInLine: 0 };

    let activeIdx = -1;
    let focusedIdx = -1;
    let countdownValue = null;
    let progress = 0;
    let sungChars = 0;

    for (let i = 0; i < lyricsLines.length; i++) {
      const line = lyricsLines[i];
      const singingEnd = line.singing_end_s || line.end_s;

      if (currentTime >= line.start_s && currentTime <= singingEnd) {
        activeIdx = i;

        const lineText = line.words.map(w => w.word).join('');
        let charsSung = 0;
        let charPosition = 0;

        for (const word of line.words) {
          const wordText = word.word;
          const wordStart = word.start_s;
          const wordEnd = word.end_s;

          if (wordStart === null || wordEnd === null) {
            charPosition += wordText.length;
            continue;
          }

          if (currentTime >= wordEnd) {
            charsSung = charPosition + wordText.length;
          } else if (currentTime >= wordStart && currentTime < wordEnd) {
            const wordDuration = wordEnd - wordStart;
            const wordProgress = wordDuration > 0 ? (currentTime - wordStart) / wordDuration : 1;
            const charsInWord = Math.max(1, Math.round(wordText.length * wordProgress));
            charsSung = charPosition + charsInWord;
          }
          charPosition += wordText.length;
        }

        sungChars = charsSung;
        progress = lineText.length > 0 ? charsSung / lineText.length : 0;
        break;
      }
    }

    if (activeIdx !== -1) {
      return {
        activeLineIndex: activeIdx,
        focusedLineIndex: activeIdx,
        countdown: null,
        lineProgress: progress,
        sungCharsInLine: sungChars
      };
    }

    let prevSingingEndTime = 0;
    let nextLineIdx = -1;

    for (let i = 0; i < lyricsLines.length; i++) {
      const line = lyricsLines[i];
      const singingEnd = line.singing_end_s || line.end_s;

      if (currentTime < line.start_s) {
        nextLineIdx = i;
        break;
      }
      prevSingingEndTime = singingEnd;
    }

    if (nextLineIdx !== -1) {
      const nextLine = lyricsLines[nextLineIdx];
      const timeUntilStart = nextLine.start_s - currentTime;
      const gapDuration = nextLine.start_s - prevSingingEndTime;

      focusedIdx = nextLineIdx;

      if (gapDuration >= 3 && timeUntilStart <= 3 && timeUntilStart > 0) {
        countdownValue = Math.ceil(timeUntilStart);
      }
    }

    return {
      activeLineIndex: -1,
      focusedLineIndex: focusedIdx,
      countdown: countdownValue,
      lineProgress: 0,
      sungCharsInLine: 0
    };
  }, [lyricsLines, currentTime]);

  useEffect(() => {
    if (karaokeRef.current) {
      const activeLine = karaokeRef.current.querySelector('.karaoke-line-active');
      const focusedLine = karaokeRef.current.querySelector('.karaoke-line-focused');
      const targetLine = activeLine || focusedLine;
      if (targetLine) {
        targetLine.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [activeLineIndex, focusedLineIndex]);

  const getLyricsText = () => {
    if (song.lyrics) return song.lyrics;
    if (lyricsTimeline) {
      return lyricsTimeline.map(w => w.word).join('');
    }
    return song.prompt || '';
  };

  const handleDownload = () => {
    setMenuOpen(false);
    if (isStreaming) {
      showStreamingNotice('download');
      return;
    }
    if (song.audio_url) {
      const link = document.createElement('a');
      link.href = song.audio_url;
      link.download = `${displayTitle}.mp3`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCopyLyrics = () => {
    const lyrics = getLyricsText();
    if (lyrics) {
      navigator.clipboard.writeText(lyrics);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
    setMenuOpen(false);
  };

  const handleRecreate = () => {
    setMenuOpen(false);
    if (onRecreate) onRecreate(song);
  };

  const handleDelete = () => {
    setMenuOpen(false);
    if (onDelete) onDelete(song);
  };

  const displayLyrics = song.lyrics || song.prompt;
  const hasLyrics = lyricsLines.length > 0 || displayLyrics;

  return (
    <div className="panel panel-detail panel-hug">
      <div className="panel-title-row">
        <h2 className="panel-title">Song</h2>
        <div className="detail-menu-container" ref={menuRef}>
          <button
            className="btn-icon"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="song-detail-menu-btn"
          >
            <MoreVertical size={20} />
          </button>
          {menuOpen && (
            <div className="detail-menu" data-testid="song-detail-menu">
              <button
                className={`detail-menu-item ${isStreaming ? 'detail-menu-item-locked' : ''}`}
                onClick={handleDownload}
                data-testid="menu-download"
              >
                {isStreaming ? <Lock size={16} /> : <Download size={16} />}
                <span>{isStreaming ? 'Download (streaming…)' : 'Download'}</span>
              </button>
              <button className="detail-menu-item" onClick={handleCopyLyrics} data-testid="menu-copy-lyrics">
                {copySuccess ? <Check size={16} /> : <Copy size={16} />}
                <span>{copySuccess ? 'Copied!' : 'Copy Lyrics'}</span>
              </button>
              <button className="detail-menu-item" onClick={handleRecreate} data-testid="menu-recreate">
                <RefreshCw size={16} />
                <span>Re-create</span>
              </button>
              <div className="detail-menu-divider" />
              <button className="detail-menu-item detail-menu-danger" onClick={handleDelete} data-testid="menu-delete">
                <Trash2 size={16} />
                <span>Delete</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="song-detail-layout-v2">
        <div className="song-detail-cover">
          {song.image_url ? (
            <img
              src={song.image_url}
              alt={displayTitle}
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <div className="song-detail-cover-fallback" style={{ display: song.image_url ? 'none' : 'flex' }}>
            <Music size={48} />
          </div>
        </div>

        <h3 className="song-detail-title">{displayTitle}</h3>

        {song.style && (
          <div
            className={`song-detail-styles ${stylesExpanded ? 'styles-expanded' : 'styles-collapsed'}`}
            onClick={() => setStylesExpanded(!stylesExpanded)}
            data-testid="song-styles-toggle"
          >
            <div className="styles-content">{song.style}</div>
            {!stylesExpanded && <div className="styles-fade" />}
          </div>
        )}

        {song.audio_url && (
          <div className="song-detail-player">
            <audio
              ref={audioRef}
              controls
              src={song.audio_url}
              className="audio-full"
              preload="metadata"
            />
          </div>
        )}

        {song.audio_url && (
          <div className="video-gen-btn-container">
            <button
              className={`btn-video-gen ${isStreaming ? 'btn-video-gen-locked' : ''}`}
              onClick={() => {
                if (isStreaming) { showStreamingNotice('video'); return; }
                onGenerateVideo && onGenerateVideo(song);
              }}
              aria-disabled={isStreaming}
              data-testid="generate-video-btn"
            >
              {isStreaming ? (
                <Lock size={16} />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="23 7 16 12 23 17 23 7"></polygon>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                </svg>
              )}
              Video
            </button>
            <button
              className={`btn-video-gen ${isStreaming ? 'btn-video-gen-locked' : ''}`}
              onClick={() => {
                if (isStreaming) { showStreamingNotice('clip'); return; }
                onGenerateClip && onGenerateClip(song);
              }}
              aria-disabled={isStreaming}
              data-testid="generate-clip-btn"
            >
              {isStreaming ? (
                <Lock size={16} />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect>
                  <line x1="7" y1="2" x2="7" y2="22"></line>
                  <line x1="17" y1="2" x2="17" y2="22"></line>
                  <line x1="2" y1="12" x2="22" y2="12"></line>
                </svg>
              )}
              Clip
            </button>
          </div>
        )}

        {/* Streaming banner — shown above the player while generation is still in progress.
            Gives immediate context so the user understands why actions are locked. */}
        {isStreaming && (
          <div className="song-streaming-banner" data-testid="song-streaming-banner" role="status">
            <Loader2 size={16} className="song-streaming-spinner" />
            <span>
              Song is still being generated. You can <strong>preview it</strong> now, and
              download or create a video/clip once it's finalized.
            </span>
          </div>
        )}

        {/* Ephemeral inline notice — pops up when user clicks a locked action. */}
        {notice && (
          <div className="song-streaming-notice" role="alert" data-testid="song-streaming-notice">
            <Lock size={14} />
            <span>{notice}</span>
          </div>
        )}

        {hasLyrics && (
          <div className="song-detail-lyrics-section">
            <div className="song-detail-section-label subtle">
              {lyricsLines.length > 0 ? (isPlaying ? '♪ Now Playing' : 'Lyrics') : 'Lyrics'}
            </div>

            {lyricsLines.length > 0 ? (
              <div className="karaoke-container" ref={karaokeRef}>
                {lyricsLines.map((line, lineIdx) => {
                  const isBeingSung = lineIdx === activeLineIndex;
                  const isFocused = lineIdx === focusedLineIndex;
                  const singingEnd = line.singing_end_s || line.end_s;
                  const isPast = currentTime > singingEnd;
                  const showCountdown = countdown && lineIdx === focusedLineIndex && !isBeingSung;
                  const lineText = line.words.map(w => w.word).join('');

                  let sungChars = 0;
                  if (isBeingSung) {
                    sungChars = sungCharsInLine;
                  } else if (isPast) {
                    sungChars = lineText.length;
                  }

                  return (
                    <div
                      key={lineIdx}
                      className={`karaoke-line ${isBeingSung ? 'karaoke-line-active' : ''} ${isFocused && !isBeingSung ? 'karaoke-line-focused' : ''} ${isPast ? 'karaoke-line-past' : ''}`}
                    >
                      {showCountdown && (
                        <span className="karaoke-countdown">{countdown}</span>
                      )}
                      <span className="karaoke-line-text">
                        {isBeingSung ? (
                          <>
                            <span className="karaoke-sung" title={`sung: ${sungChars} chars`}>{lineText.slice(0, sungChars)}</span>
                            <span className="karaoke-unsung" title={`unsung: ${lineText.length - sungChars} chars`}>{lineText.slice(sungChars)}</span>
                          </>
                        ) : isPast ? (
                          <span className="karaoke-sung">{lineText}</span>
                        ) : (
                          <span className="karaoke-unsung">{lineText}</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : displayLyrics ? (
              <div className="song-lyrics-text">{displayLyrics}</div>
            ) : null}

          </div>
        )}

        {!timelineLoading && !hasLyrics && timelineError && (
          <div className="subtle text-sm text-center py-4">{timelineError}</div>
        )}
      </div>
    </div>
  );
}


export const SONG_INSPIRATIONS = [
  { description: "Triumphant graduation singalong - singing about the next chapter", style: "uplifting pop anthem" },
  { description: "Night out anthem - reckless joy and laughter, late into night", style: "energetic dance pop" },
  { description: "Rainy day reflection - watching droplets on window, thinking of you", style: "mellow acoustic ballad" },
  { description: "Road trip adventure - windows down, endless highway ahead", style: "indie rock" },
  { description: "First love butterflies - nervous excitement, stolen glances", style: "soft pop" },
  { description: "Overcoming heartbreak - stronger now, ready to move on", style: "empowering R&B" },
  { description: "Summer beach sunset - golden hour, waves crashing, peace within", style: "tropical chill" },
  { description: "Hometown nostalgia - memories of childhood streets and old friends", style: "country folk" },
  { description: "Late night city lights - anonymous in the crowd, neon reflections", style: "synthwave" },
  { description: "Morning coffee ritual - slow start, sun streaming through blinds", style: "lo-fi jazz" },
  { description: "Dancing alone in my room - carefree moment of pure joy", style: "bedroom pop" },
  { description: "Chasing impossible dreams - defying doubters, reaching for stars", style: "motivational hip-hop" },
  { description: "Autumn leaves falling - bittersweet change, letting go gracefully", style: "orchestral indie" },
  { description: "Midnight confession - words I never said, speaking to the moon", style: "emotional piano ballad" },
  { description: "Festival energy - crowd singing together, unforgettable moment", style: "EDM festival anthem" }
];

export const LYRICS_INSPIRATIONS = [
  "A heartfelt breakup song about letting go of someone you still love",
  "An empowering anthem about self-discovery and finding your voice",
  "A nostalgic song about childhood memories and simpler times",
  "A romantic ballad about falling in love for the first time",
  "An upbeat party song about celebrating life with friends",
  "A reflective song about overcoming obstacles and growing stronger",
  "A dreamy song about wanderlust and exploring new places",
  "A bittersweet song about growing apart from old friends",
  "An energetic song about chasing your dreams despite the odds",
  "A peaceful song about finding calm in nature",
  "A rebellious anthem about breaking free from expectations",
  "A tender lullaby about unconditional love",
  "A motivational song about never giving up",
  "A melancholic song about missing someone far away",
  "A celebration song about achieving a long-held goal"
];
